package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.Assignment
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.PostAttachment
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import com.elan.edubridge.ui.staff.Toast
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement

/** `/app/me/assignments` — 과제 목록 + 학원이 올린 자료. 4탭 어디에도 속하지 않는 하위 화면이라
 * [MemberShell]에는 `activeTab = null`을 넘긴다(가이드 §3.7).
 *
 * 자료는 문서에 인라인돼 있어 목록을 그리는 데 추가 조회가 없고, 파일을 여는 순간에만
 * 5분짜리 서명 URL을 받는다(`getAssignmentAttachmentUrl`) — Storage 규칙은 학원 멤버십을
 * 판정할 수 없어서 그 콜러블이 유일한 열람 경로다. */
object MyAssignmentsPage {
    private val scope = MainScope()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    /** 과제 조회 실패의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "member-assignments-retry"

    fun render(session: SessionState) {
        scope.launch {
            val context = loadMemberContext(session)
            val assignments = load(context)
            MemberShell.render(context, activeTab = null, bodyHtml = body(context, assignments))
            wireRetry(RETRY_ID) { render(session) }
            wireMaterials(assignments)
        }
    }

    /** 과제와 그 학원 id를 함께 들고 다닌다 — 자료를 열 때 콜러블이 학원을 요구한다. */
    private class Row(val academyId: String, val assignment: Assignment)

    /** 학원별로 따로 읽으므로 일부만 실패할 수 있다 — 첫 실패를 남겨 화면이 "없음"과 구분한다(#30). */
    private class Loaded(val rows: List<Row>, val failure: CommandFailure?)

    private suspend fun load(context: MemberContext): Loaded {
        var failure: CommandFailure? = null
        val rows = context.subjectAssignmentTargets()
            .flatMap { (academyId, studentUserId) ->
                val result = context.repository.studentAssignmentsResult(academyId, studentUserId)
                if (failure == null) failure = result.failure
                result.items.map { Row(academyId, it) }
            }
            .distinctBy { it.assignment.id }
            .sortedBy { it.assignment.dueDate }
        return Loaded(rows, failure)
    }

    private fun body(context: MemberContext, loaded: Loaded): String {
        if (context.subjectAssignmentTargets().isEmpty()) {
            return emptyState("아직 연결된 학원이 없어요", "학원에서 보낸 초대 링크를 받으면 과제가 여기에 나타나요.")
        }
        if (loaded.rows.isEmpty()) {
            val readFailure = loaded.failure
            return if (readFailure != null) {
                readErrorHtml(readFailure, RETRY_ID)
            } else {
                emptyState("아직 과제가 없어요", "선생님이 과제를 올리면 여기에 나타나요.")
            }
        }

        val rows = loaded.rows.joinToString("") { row ->
            val assignment = row.assignment
            val statusChip = if (assignment.completed) {
                """<span class="eb-chip eb-chip--success">완료</span>"""
            } else {
                """<span class="eb-chip eb-chip--warning">진행 중</span>"""
            }
            // 자료는 문서에 인라인돼 있다 — 목록을 그리는 데 추가 조회가 없다.
            val materials = assignment.attachments.joinToString("") { attachment ->
                """<span class="eb-chip eb-chip--info"><button class="eb-button eb-button--link member-material" type="button" data-assignment-id="${assignment.id.escapeHtml()}" data-attachment-id="${attachment.id.escapeHtml()}">${attachment.fileName.escapeHtml()}</button></span>"""
            }
            """
            <article class="eb-assignment-item">
                ${icon("icon-assignment", "md")}
                <div>
                    <strong>${assignment.title.escapeHtml()}</strong>
                    <span class="eb-meta">${assignment.teacher.escapeHtml()} · 마감 ${assignment.dueDate.escapeHtml()}</span>
                    ${if (materials.isEmpty()) "" else """<div class="eb-chip-row">$materials</div>"""}
                </div>
                $statusChip
            </article>
            """.trimIndent()
        }

        return """
            <h1 class="eb-page-title">과제</h1>
            <section class="eb-card">$rows</section>
        """.trimIndent()
    }

    private fun wireMaterials(loaded: Loaded) {
        val buttons = document.querySelectorAll(".member-material")
        for (index in 0 until buttons.length) {
            val button = buttons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                val assignmentId = button.getAttribute("data-assignment-id")
                val attachmentId = button.getAttribute("data-attachment-id")
                val row = loaded.rows.firstOrNull { it.assignment.id == assignmentId }
                val attachment = row?.assignment?.attachments?.firstOrNull { it.id == attachmentId }
                if (row != null && attachment != null) openMaterial(row, attachment)
                null
            }
        }
    }

    /** 서명 URL은 5분짜리라 저장하지 않고 누를 때마다 새로 받는다. */
    private fun openMaterial(row: Row, attachment: PostAttachment) {
        scope.launch {
            val ticket = commandRepository.getAssignmentAttachmentUrl(
                row.academyId,
                row.assignment.id,
                attachment.id,
            )
            val url = ticket.downloadUrl
            if (!ticket.isSuccess || url == null) {
                Toast.error(ticket.errorMessage ?: "자료를 열지 못했어요. 다시 시도해 주세요.")
                return@launch
            }
            val anchor = document.createElement("a") as HTMLAnchorElement
            anchor.href = url
            anchor.target = "_blank"
            anchor.rel = "noopener"
            document.body?.appendChild(anchor)
            anchor.click()
            anchor.remove()
        }
    }

    private fun emptyState(title: String, body: String): String = """
        <div class="eb-empty-state">
            ${icon("icon-assignment", "xl")}
            <h2>${title.escapeHtml()}</h2>
            <p>${body.escapeHtml()}</p>
        </div>
    """.trimIndent()
}
